import type { Tracker } from '../tracker'

export interface HeapOptions {
  disableTextCapture?: boolean
  secureCookie?: boolean
  trackingServer?: string
  captureIp?: boolean
}

export interface HeapTrackerConfig {
  appId: string
  options?: HeapOptions
}

function loadHeap(appId: string, options?: HeapOptions): Promise<any> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Heap can only run in browser'))
      return
    }
    const w = window as any
    if (w.heap && w.heap.appid === appId) {
      resolve(w.heap)
      return
    }
    w.heap = w.heap || []
    w.heap.load = (envId: string, config?: any) => {
      w.heap.appid = envId
      w.heap.config = config || {}
      const s = document.createElement('script')
      s.type = 'text/javascript'
      s.async = true
      s.src = 'https://cdn.heapanalytics.com/js/heap-' + envId + '.js'
      s.onload = () => resolve(w.heap)
      s.onerror = () => reject(new Error('Failed to load Heap script'))
      const f = document.getElementsByTagName('script')[0]
      f?.parentNode?.insertBefore(s, f)
    }
    w.heap.load(appId, options)
  })
}

export function heapTracker(config: HeapTrackerConfig): Tracker {
  let heap: any | null = null
  let ready = false
  const queue: Array<() => void> = []

  function run(fn: () => void) {
    if (ready && heap) fn()
    else queue.push(fn)
  }

  loadHeap(config.appId, config.options)
    .then((h) => {
      heap = h
      ready = true
      queue.splice(0).forEach((fn) => fn())
    })
    .catch(() => {
      // keep console fallback
      ready = true
      queue.splice(0).forEach((fn) => fn())
    })

  return {
    track(event, payload, ctx) {
      run(() => {
        if (!heap) {
          // eslint-disable-next-line no-console
          console.log('[heap-fallback]', event, payload, ctx)
          return
        }
        try {
          const props = { ...(payload as any), ...(ctx || {}) }
          heap.track(event, props)
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[heap] track failed', e)
        }
      })
    },
    identify(userId, traits) {
      run(() => {
        if (!heap) {
          // eslint-disable-next-line no-console
          console.log('[heap-fallback][identify]', userId, traits || {})
          return
        }
        try {
          if (userId) heap.identify(userId)
          if (traits && typeof heap.addUserProperties === 'function') heap.addUserProperties(traits)
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn('[heap] identify failed', e)
        }
      })
    },
  }
}
